/**
 * OPEX Analysis Detail Section - Premium Board-Level Design
 * De Beers-Inspired Visual Authority System
 * ABC - Empresa ABC
 */

import React, { useState } from 'react';
import { ArrowLeft, TrendingDown, TrendingUp, Users, Package, Building, Truck, BarChart3, ArrowUpRight, ArrowDownRight, Minus, FileText, Target, Info } from 'lucide-react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart as RechartsPie, Pie, Legend, Line, ReferenceLine
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  },
  chart: {
    primary: '#0D1538',
    secondary: '#B89A3C',
    tertiary: '#0C8A4B',
    quaternary: '#3B82F6',
    quinary: '#8B5CF6',
  }
};

const typography = {
  pageTitle: 'font-serif text-[32px] leading-[40px] font-semibold tracking-[-0.3px]',
  sectionTitle: 'font-serif text-[20px] leading-[26px] font-semibold tracking-[-0.2px]',
  cardLabel: 'font-sans text-[11px] leading-[14px] font-semibold tracking-[0.4px]',
  kpiValue: 'font-sans text-[26px] leading-[30px] font-semibold tracking-[-0.5px]',
  bodyText: 'font-sans text-[14px] leading-[22px] font-normal',
  smallText: 'font-sans text-[12px] leading-[16px] font-normal',
  microText: 'font-sans text-[10px] leading-[14px] font-medium tracking-[0.3px] uppercase',
};

const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Executive Summary Item Component - Premium
const ExecutiveSummaryItem = ({ number, text }) => (
  <div className="flex items-start gap-3">
    <div 
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: colors.bg.dark }}
    >
      <span className="text-[12px] font-semibold" style={{ color: colors.accent.gold }}>{number}</span>
    </div>
    <p className={typography.bodyText} style={{ color: colors.text.secondary, paddingTop: '2px' }}>{text}</p>
  </div>
);

// Custom Legend for Premium Pie Chart
const CustomPieLegend = ({ payload }) => (
  <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
    {payload.map((entry, index) => (
      <div key={index} className="flex items-center gap-2">
        <div 
          className="w-3 h-3 rounded flex-shrink-0" 
          style={{ backgroundColor: entry.color }}
        />
        <span className={typography.smallText} style={{ color: colors.text.secondary }}>{entry.value}</span>
      </div>
    ))}
  </div>
);

const OPEXDetail = ({ year, language, setActiveSection, flowMap }) => {
  const opexData = mockData.opexBreakdown[year];
  const prevOpexData = mockData.opexBreakdown[year - 1] || mockData.opexBreakdown[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? (language === 'pt' ? 'vs Orç. 1T' : 'vs Bud. 1Q') : (language === 'pt' ? 'vs Orç.' : 'vs Bud.');
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // State for dumbbell tooltip
  const [hoveredDot, setHoveredDot] = useState(null);
  
  // Use 'total' instead of 'totalOpex' as per mockData structure
  const totalOpex = opexData.total;
  const prevTotalOpex = prevOpexData.total;
  
  // Budget data for OPEX
  const budgetOpex = budgetData.opex?.budget || totalOpex * 0.98;
  const opexVsBudget = ((totalOpex - budgetOpex) / budgetOpex * 100).toFixed(1);
  const opexVsBudgetAbs = (totalOpex - budgetOpex).toFixed(1);

  // Map FSE to Gerais e Administrativos in category names
  const mapCategoryName = (name) => {
    if (name === 'FSE') return language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative';
    if (name === 'Supplies & External Services') return language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative';
    return name;
  };

  const opexKeyInsights = [
    { 
      driver: language === 'pt' ? 'Eficiência Operacional' : 'Operational Efficiency',
      detail: language === 'pt' 
        ? `Custos Op./Receita melhorou de ${prevOpexData.ratios.opexToRevenue}% para ${opexData.ratios.opexToRevenue}%.`
        : `Op. Costs/Revenue improved from ${prevOpexData.ratios.opexToRevenue}% to ${opexData.ratios.opexToRevenue}%.`,
      icon: TrendingDown,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/30'
    },
    { 
      driver: language === 'pt' ? 'Custos com Pessoal' : 'Personnel Costs',
      detail: language === 'pt' 
        ? `${opexData.categories[0].percentage}% dos Custos Op. totais. Produtividade +8% YoY.`
        : `${opexData.categories[0].percentage}% of total Op. Costs. Productivity +8% YoY.`,
      icon: Users,
      color: 'text-gold-400',
      bgColor: 'bg-amber-500/30'
    },
    { 
      driver: language === 'pt' ? 'Controlo de Materiais' : 'Materials Control',
      detail: language === 'pt' 
        ? `Custos em ${opexData.ratios.materialsToRevenue}% da receita. Gestão optimizada.`
        : `Costs at ${opexData.ratios.materialsToRevenue}% of revenue. Optimized management.`,
      icon: Package,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/30'
    },
  ];

  // Premium pie chart colors
  const pieColors = [colors.chart.primary, colors.chart.secondary, colors.chart.tertiary, colors.chart.quaternary, colors.chart.quinary];

  // Prepare pie data with translated names - use name[language] from mockData
  const pieData = opexData.categories.map((cat, idx) => {
    let displayName = cat.name[language] || cat.name.pt;
    // Map FSE to Gerais e Administrativos
    if (displayName === 'FSE' || displayName === 'Supplies & External Services') {
      displayName = language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative';
    }
    return {
      name: displayName,
      value: cat.value,
      percentage: cat.percentage,
      fill: pieColors[idx % pieColors.length]
    };
  });

  // Calculate changes for dumbbell chart
  const opexChange = Math.round((totalOpex / prevTotalOpex - 1) * 100);
  const ratioChange = (prevOpexData.ratios.opexToRevenue - opexData.ratios.opexToRevenue).toFixed(1);

  // Calculate OPEX per carat (regular carats sold)
  const costPerCaratData = mockData.costPerCarat?.[year] || {};
  const prevCostPerCaratData = mockData.costPerCarat?.[year - 1] || {};
  const regularCarats = costPerCaratData.kpis?.regularCarats?.value || 920; // in thousands
  const prevRegularCarats = prevCostPerCaratData.kpis?.regularCarats?.value || 820;
  
  // OPEX per carat = Total OPEX (M USD) / Regular Carats (k) * 1000 = USD per carat
  const opexPerCarat = regularCarats > 0 ? Math.round((totalOpex * 1000) / regularCarats) : 0;
  const prevOpexPerCarat = prevRegularCarats > 0 ? Math.round((prevTotalOpex * 1000) / prevRegularCarats) : 0;
  const opexPerCaratChange = prevOpexPerCarat > 0 ? ((opexPerCarat - prevOpexPerCarat) / prevOpexPerCarat) * 100 : 0;

  // Prepare Dumbbell chart data - OPEX by Category 2025 vs 2024
  const dumbbellData = opexData.categories.map((cat, idx) => {
    const prevCat = prevOpexData.categories[idx];
    const prevValue = prevCat?.value || 0;
    const currentValue = cat.value;
    const change = currentValue - prevValue;
    const changePercent = prevValue > 0 ? ((change / prevValue) * 100) : 0;
    let displayName = cat.name[language] || cat.name.pt;
    // Map FSE to Gerais e Administrativos
    if (displayName === 'FSE' || displayName === 'Supplies & External Services') {
      displayName = language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative';
    }
    return {
      name: displayName,
      prevYear: prevValue,
      currentYear: currentValue,
      change: change,
      changePercent: parseFloat(changePercent.toFixed(1)),
      // For the dumbbell visual - min and max for positioning
      min: Math.min(prevValue, currentValue),
      max: Math.max(prevValue, currentValue),
    };
  }).sort((a, b) => b.currentYear - a.currentYear); // Sort by current value descending

  // Colors for dumbbell chart
  const prevYearColor = '#94A3B8'; // Slate gray for previous year
  const currentYearColor = '#0A192F'; // Primary dark for current year

  // Calculate category YoY changes
  // Note: In mockData, categories[0] = CMVMC, categories[1] = Personnel
  const cmvmcYoY = prevOpexData.categories[0]?.value 
    ? ((opexData.categories[0].value - prevOpexData.categories[0].value) / prevOpexData.categories[0].value * 100).toFixed(1) 
    : 0;
  const personnelYoY = prevOpexData.categories[1]?.value 
    ? ((opexData.categories[1].value - prevOpexData.categories[1].value) / prevOpexData.categories[1].value * 100).toFixed(1) 
    : 0;
  const opexYoY = prevTotalOpex 
    ? ((totalOpex - prevTotalOpex) / prevTotalOpex * 100).toFixed(1) 
    : 0;

  // Budget values for categories (estimates)
  // categories[0] = CMVMC, categories[1] = Personnel
  const budgetCMVMC = opexData.categories[0].value * 1.015;
  const budgetPersonnel = opexData.categories[1].value * 0.97;
  const cmvmcVsBudget = ((opexData.categories[0].value - budgetCMVMC) / budgetCMVMC * 100).toFixed(1);
  const personnelVsBudget = ((opexData.categories[1].value - budgetPersonnel) / budgetPersonnel * 100).toFixed(1);

  // Executive Summary content - Análise por Rubrica de Custos Operacionais
  const executiveAnalysisSections = [
    {
      icon: Package,
      title: { pt: 'CMVMC', en: 'COGS' },
      intro: {
        pt: 'O CMVMC ficou [x]% acima/abaixo do período homólogo e [x]% acima/abaixo do orçamento.',
        en: 'COGS was [x]% above/below the prior period and [x]% above/below budget.'
      },
      vsHomologo: {
        pt: 'Face ao homólogo, o desvio deve ser lido sobretudo pela combinação entre gasóleo, peças e manutenção, explosivos e pneus e consumíveis de tratamento. O ponto central é separar o que resultou de maior intensidade operacional — mais movimento de estéril, maior utilização da frota e maior minério tratado — do que resultou de preço unitário ou de eficiência operacional. O gasóleo continua a ser o principal driver económico da rubrica; peças, manutenção e pneus são o melhor indicador da qualidade da utilização da frota; e os consumíveis de tratamento mostram o efeito de escala da planta.',
        en: 'Compared to the prior period, the variance should be read mainly through the combination of diesel, parts and maintenance, explosives, tires, and processing consumables. The central point is to separate what resulted from higher operational intensity from what resulted from unit price or operational efficiency.'
      },
      vsOrcamento: {
        pt: 'Face ao orçamento, o desvio deve ser explicado por quatro variáveis: nível de actividade (m³ movimentados, minério tratado e horas de equipamento); preço unitário (sobretudo combustível); disponibilidade e desgaste da frota (manutenção, peças e pneus); consumo específico da planta (DMS ferro-silício e outros consumíveis).',
        en: 'Against budget, the variance should be explained by four variables: activity level; unit price; fleet availability and wear; and plant-specific consumption.'
      },
      leituraGestao: {
        pt: 'Se o desvio veio de maior actividade com consumo específico controlado, a leitura é operacional; se o desvio veio de combustível, manutenção e pneus acima da cadência operacional, o tema passa a ser eficiência e disciplina de utilização; se o desvio veio de consumíveis de tratamento, deve ser lido em paralelo com minério tratado e recuperação.',
        en: 'If the variance came from higher activity with controlled specific consumption, the reading is operational; if from fuel, maintenance and tires above operational pace, the topic becomes efficiency and usage discipline.'
      }
    },
    {
      icon: Users,
      title: { pt: 'Custos com Pessoal', en: 'Personnel Costs' },
      intro: {
        pt: 'Os custos com pessoal ficaram [x]% acima/abaixo do período homólogo e [x]% acima/abaixo do orçamento.',
        en: 'Personnel costs were [x]% above/below the prior period and [x]% above/below budget.'
      },
      vsHomologo: {
        pt: 'Face ao homólogo, o desvio deve ser decomposto entre número efectivo de colaboradores, actualização remuneratória, encargos sociais, benefícios e eventual uso de extraordinário/turnos. O ponto de análise não é a rubrica agregada, mas o custo médio por colaborador e o alinhamento entre reforço de estrutura e nível de actividade.',
        en: 'Compared to the prior period, the variance should be broken down between effective headcount, salary updates, social charges, benefits, and overtime/shift usage.'
      },
      vsOrcamento: {
        pt: 'Face ao orçamento, a explicação deve centrar-se em cinco temas: calendarização das contratações (entradas antes ou depois do previsto); diferença entre quadro planeado e quadro efectivo; actualizações salariais e encargos acima do previsto; uso adicional de horas extra ou turnos; benefícios e despesas acessórias por colaborador.',
        en: 'Against budget, the explanation should focus on five themes: hiring calendar; planned vs effective headcount; salary updates above planned; additional overtime usage; and benefits per employee.'
      },
      leituraGestao: {
        pt: 'Se o aumento ficou concentrado em áreas produtivas e técnicas, o desvio deve ser lido com a actividade; se o aumento ficou acima do orçamento sem correspondência em produção, manutenção ou controlo, o tema passa a ser produtividade da estrutura; se o desvio veio de encargos e benefícios, a análise deve separar efeito de política remuneratória de efeito de headcount.',
        en: 'If the increase was concentrated in productive and technical areas, the variance should be read with activity; if above budget without correspondence in production, the topic becomes structure productivity.'
      }
    },
    {
      icon: Truck,
      title: { pt: 'Subcontratos', en: 'Subcontracts' },
      intro: {
        pt: 'Os subcontratos ficaram [x]% acima/abaixo do período homólogo e [x]% acima/abaixo do orçamento.',
        en: 'Subcontracts were [x]% above/below the prior period and [x]% above/below budget.'
      },
      vsHomologo: {
        pt: 'Face ao homólogo, a leitura deve centrar-se no maior ou menor recurso a serviços técnicos e especializados, nomeadamente apoio à exploração, engenharia, projectos, sondagens, assistência técnica e outros serviços de suporte externo. Nesta rubrica, o desvio pode reflectir flexibilização da capacidade interna ou maior dependência de terceiros; é essa distinção que importa fazer.',
        en: 'Compared to the prior period, the reading should focus on greater or lesser use of technical and specialized services. The variance may reflect flexibilization of internal capacity or greater dependence on third parties.'
      },
      vsOrcamento: {
        pt: 'Face ao orçamento, os principais pontos de explicação são: antecipação ou adiamento de trabalhos especializados; alteração de âmbito de projectos e serviços; necessidade de recorrer a capacidade externa não prevista; substituição de capacidade interna por externa; normalização de serviços extraordinários face ao ano anterior.',
        en: 'Against budget, the main explanation points are: early or delayed specialized work; scope changes; need for unplanned external capacity; replacement of internal by external capacity.'
      },
      leituraGestao: {
        pt: 'Se o desvio veio de suporte técnico directamente ligado à operação, deve ser lido com continuidade operacional e execução; se veio de serviços recorrentes sem ligação clara à produção, manutenção ou controlo, o tema passa a ser disciplina de spending; se o recurso a subcontratos substituiu contratação interna, a análise deve ser feita em conjunto com custos com pessoal.',
        en: 'If the variance came from technical support directly linked to operations, it should be read with operational continuity; if from recurring services without clear link to production, the topic becomes spending discipline.'
      }
    },
    {
      icon: Building,
      title: { pt: 'Gerais e Administrativos', en: 'General & Administrative' },
      intro: {
        pt: 'Os gastos gerais e administrativos ficaram [x]% acima/abaixo do período homólogo e [x]% acima/abaixo do orçamento.',
        en: 'General and administrative expenses were [x]% above/below the prior period and [x]% above/below budget.'
      },
      vsHomologo: {
        pt: 'Face ao homólogo, esta rubrica deve ser explicada pela evolução de serviços de suporte, seguros e responsabilidade corporativa, TI, comunicações e publicidade, bem como deslocações, honorários e outros custos de estrutura. A leitura útil não é apenas "cresceu/caiu", mas identificar qual o bloco funcional que explica o desvio.',
        en: 'Compared to the prior period, this item should be explained by evolution of support services, insurance, IT, communications, as well as travel, fees and other structure costs.'
      },
      vsOrcamento: {
        pt: 'Face ao orçamento, os principais drivers a testar são: maior ou menor recurso a consultoria, auditoria e assessoria especializada; licenças informáticas e implementação de sistemas; seguros, vigilância e requisitos de suporte institucional; deslocações, comunicações e despesas de estrutura; custos ligados a iniciativas não recorrentes.',
        en: 'Against budget, the main drivers to test are: consulting, audit and advisory usage; software licenses and system implementation; insurance, security and institutional support; travel and structure expenses.'
      },
      leituraGestao: {
        pt: 'Se o desvio veio de TI, assistência técnica e sistemas, a explicação está normalmente em reforço de controlo e integração de processos; se veio de honorários e estudos, importa separar trabalho recorrente de trabalho pontual; se veio de custos de estrutura com pouca ligação à execução, o tema passa a ser racionalização administrativa.',
        en: 'If the variance came from IT and systems, the explanation is usually control reinforcement; if from fees and studies, separate recurring from one-time work; if from structure costs with little link to execution, the topic becomes administrative rationalization.'
      }
    },
    {
      icon: BarChart3,
      title: { pt: 'Custos de Comercialização', en: 'Commercial Costs' },
      intro: {
        pt: 'Os custos de comercialização ficaram [x]% acima/abaixo do período homólogo e [x]% acima/abaixo do orçamento.',
        en: 'Commercial costs were [x]% above/below the prior period and [x]% above/below budget.'
      },
      vsHomologo: {
        pt: 'Face ao homólogo, esta rubrica deve ser lida quase exclusivamente em função de valor vendido e mix de vendas, uma vez que resulta de royalties e comissões de intermediação ligadas à comercialização. Ou seja, nesta linha, o desvio económico tende a ser mais um tema de receita e composição das vendas do que de custo operacional em sentido estrito.',
        en: 'Compared to the prior period, this item should be read almost exclusively as a function of sales value and sales mix, since it results from royalties and intermediation commissions linked to commercialization.'
      },
      vsOrcamento: {
        pt: 'Face ao orçamento, os drivers a explicar são: desvio do valor vendido face ao plano; mix entre pedras regulares e especiais; calendarização das vendas e leilões; diferença entre venda bruta e montante efectivamente sujeito a retenções e comissões.',
        en: 'Against budget, the drivers to explain are: sales value deviation from plan; mix between regular and special stones; sales and auction calendar; difference between gross sales and amount subject to withholdings and commissions.'
      },
      leituraGestao: {
        pt: 'Se a rubrica sobe em linha com a receita, o desvio é mecânico; se sobe mais do que a receita, o foco deve estar no mix e na reconciliação; se fica abaixo do orçamento, importa confirmar se isso reflecte menor venda, melhor mix ou apenas calendário comercial distinto do previsto.',
        en: 'If the item rises in line with revenue, the variance is mechanical; if it rises more than revenue, focus should be on mix and reconciliation; if below budget, confirm if this reflects lower sales, better mix, or just different commercial calendar.'
      }
    }
  ];

  // Texto introdutório do Resumo Executivo
  const summaryIntro = {
    pt: 'Nesta secção, a comparação com o período homólogo e com o orçamento deve responder a duas perguntas: onde está o desvio material e o que o explica em termos económicos. O detalhe numérico fica nas páginas seguintes; aqui, a leitura deve concentrar-se no significado do desvio por rubrica. As rubricas que mais importam para a interpretação da margem são CMVMC e custos de comercialização; nas restantes, o foco deve estar em perceber se o desvio reflecte maior capacidade operacional, maior recurso a serviços de suporte ou pressão estrutural na base de custos.',
    en: 'In this section, the comparison with the prior period and budget should answer two questions: where is the material deviation and what explains it in economic terms. The numerical detail is on the following pages; here, the reading should focus on the meaning of the deviation by item.'
  };

  // Custom Tooltip for premium look
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
          <p className="font-semibold text-gold-400 mb-1">{data.name}</p>
          <p className="text-sm">{data.value}M USD</p>
          <p className="text-xs text-slate-400">{data.percentage}% {language === 'pt' ? 'dos custos op.' : 'of op. costs'}</p>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip for Dumbbell chart
  const CustomDumbbellTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
          <p className="font-semibold text-gold-400 mb-2">{data.name}</p>
          <div className="space-y-1">
            <p className="text-xs flex justify-between gap-4">
              <span className="text-slate-400">{year}:</span>
              <span className="font-semibold">{data.currentYear.toFixed(1)}M USD</span>
            </p>
            <p className="text-xs flex justify-between gap-4">
              <span className="text-slate-400">{year - 1}:</span>
              <span className="font-semibold text-slate-300">{data.prevYear.toFixed(1)}M USD</span>
            </p>
            <div className="border-t border-slate-600 pt-1 mt-1">
              <p className="text-xs flex justify-between gap-4">
                <span className="text-slate-400">{language === 'pt' ? 'Variação:' : 'Change:'}</span>
                <span className={`font-semibold ${data.change > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {data.change > 0 ? '+' : ''}{data.change.toFixed(1)}M ({data.changePercent > 0 ? '+' : ''}{data.changePercent}%)
                </span>
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8" style={{ backgroundColor: colors.bg.page }} data-testid="opex-detail-section">
      {/* Header */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: colors.text.tertiary }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Custos Operacionais' : 'Operating Costs'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: colors.text.secondary }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* Key Insights - Premium Band */}
      <div 
        className="rounded-[16px] overflow-hidden"
        style={{ backgroundColor: colors.bg.dark }}
      >
        <div className="px-8 py-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
            <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
              {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-5">
            {opexKeyInsights.map((insight, idx) => (
              <div 
                key={idx} 
                className="bg-white/[0.06] rounded-[10px] p-4 border border-white/[0.08]"
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: colors.status.positiveBg }}
                  >
                    <insight.icon className="w-4.5 h-4.5" style={{ color: colors.status.positive }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[13px] mb-1 text-white/90">{insight.driver}</h4>
                    <p className="text-white/60 text-[12px] leading-relaxed">{insight.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs - Premium Cards with Trend Indicators */}
      <div className="grid grid-cols-5 gap-5">
        {/* Custos Operacionais */}
        <div 
          className="rounded-[14px] p-5 relative overflow-hidden"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          {/* Trend Arrow Indicator - Medium Size, positioned near KPI value */}
          <div 
            className="absolute top-10 right-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: opexChange > 0 ? colors.status.negativeBg : opexChange < 0 ? colors.status.positiveBg : colors.accent.goldBg
            }}
          >
            {opexChange > 0 ? (
              <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.negative }} />
            ) : opexChange < 0 ? (
              <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.positive }} />
            ) : (
              <Minus className="w-4 h-4" style={{ color: colors.accent.gold }} />
            )}
          </div>
          <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>{language === 'pt' ? 'Custos Operacionais' : 'Operating Costs'}</p>
          <p className={typography.kpiValue} style={{ color: colors.text.primary }}>{formatM(totalOpex)}<span className={typography.smallText} style={{ color: colors.text.tertiary, marginLeft: '4px' }}>M USD</span></p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: parseFloat(opexVsBudget) > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: parseFloat(opexVsBudget) > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {parseFloat(opexVsBudget) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(opexVsBudget) > 0 ? '+' : ''}{opexVsBudget}% Orç.
            </span>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: opexChange > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: opexChange > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {opexChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {opexChange > 0 ? '+' : ''}{opexChange}% YoY
            </span>
          </div>
        </div>

        {/* Custos Op./Receita */}
        <div 
          className="rounded-[14px] p-5 relative overflow-hidden"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          {/* Trend Arrow Indicator - Medium Size, positioned near KPI value */}
          <div 
            className="absolute top-10 right-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ backgroundColor: colors.status.positiveBg }}
          >
            <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.positive }} />
          </div>
          <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>{language === 'pt' ? 'Custos Op./Receita' : 'Op. Costs/Revenue'}</p>
          <p className={typography.kpiValue} style={{ color: colors.status.positive }}>{opexData.ratios.opexToRevenue}<span className={typography.smallText} style={{ color: colors.text.tertiary, marginLeft: '4px' }}>%</span></p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ backgroundColor: colors.status.positiveBg, color: colors.status.positive }}
            >
              <TrendingDown className="w-3 h-3" />
              -1.2p.p. vs Orç.
            </span>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ backgroundColor: colors.status.positiveBg, color: colors.status.positive }}
            >
              <TrendingDown className="w-3 h-3" />
              -{ratioChange}p.p. YoY
            </span>
          </div>
        </div>

        {/* CMVMC */}
        <div 
          className="rounded-[14px] p-5 relative overflow-hidden"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          {/* Trend Arrow Indicator - Medium Size, positioned near KPI value */}
          <div 
            className="absolute top-10 right-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: parseFloat(cmvmcYoY) > 0 ? colors.status.negativeBg : colors.status.positiveBg
            }}
          >
            {parseFloat(cmvmcYoY) > 0 ? (
              <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.negative }} />
            ) : (
              <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.positive }} />
            )}
          </div>
          <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>CMVMC</p>
          <p className={typography.kpiValue} style={{ color: colors.text.primary }}>{formatM(opexData.categories[0].value)}<span className={typography.smallText} style={{ color: colors.text.tertiary, marginLeft: '4px' }}>M USD</span></p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: parseFloat(cmvmcVsBudget) < 0 ? colors.status.positiveBg : colors.status.negativeBg,
                color: parseFloat(cmvmcVsBudget) < 0 ? colors.status.positive : colors.status.negative
              }}
            >
              {parseFloat(cmvmcVsBudget) < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
              {parseFloat(cmvmcVsBudget) > 0 ? '+' : ''}{cmvmcVsBudget}% Orç.
            </span>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: parseFloat(cmvmcYoY) > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: parseFloat(cmvmcYoY) > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {parseFloat(cmvmcYoY) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(cmvmcYoY) > 0 ? '+' : ''}{cmvmcYoY}% YoY
            </span>
          </div>
        </div>

        {/* Custos Pessoal */}
        <div 
          className="rounded-[14px] p-5 relative overflow-hidden"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          {/* Trend Arrow Indicator - Medium Size, positioned near KPI value */}
          <div 
            className="absolute top-10 right-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: parseFloat(personnelYoY) > 0 ? colors.status.negativeBg : colors.status.positiveBg
            }}
          >
            {parseFloat(personnelYoY) > 0 ? (
              <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.negative }} />
            ) : (
              <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.positive }} />
            )}
          </div>
          <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>{language === 'pt' ? 'Custos Pessoal' : 'Personnel'}</p>
          <p className={typography.kpiValue} style={{ color: colors.text.primary }}>{formatM(opexData.categories[1].value)}<span className={typography.smallText} style={{ color: colors.text.tertiary, marginLeft: '4px' }}>M USD</span></p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: parseFloat(personnelVsBudget) > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: parseFloat(personnelVsBudget) > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {parseFloat(personnelVsBudget) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(personnelVsBudget) > 0 ? '+' : ''}{personnelVsBudget}% Orç.
            </span>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: parseFloat(personnelYoY) > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: parseFloat(personnelYoY) > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {parseFloat(personnelYoY) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {parseFloat(personnelYoY) > 0 ? '+' : ''}{personnelYoY}% YoY
            </span>
          </div>
        </div>

        {/* Custos Op./Quilate */}
        <div 
          className="rounded-[14px] p-5 relative overflow-hidden"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          {/* Trend Arrow Indicator - Medium Size, positioned near KPI value */}
          <div 
            className="absolute top-10 right-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: opexPerCaratChange > 0 ? colors.status.negativeBg : colors.status.positiveBg
            }}
          >
            {opexPerCaratChange > 0 ? (
              <ArrowUpRight className="w-4 h-4" style={{ color: colors.status.negative }} />
            ) : (
              <ArrowDownRight className="w-4 h-4" style={{ color: colors.status.positive }} />
            )}
          </div>
          <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>{language === 'pt' ? 'Custos Op./Quilate' : 'Op. Costs/Carat'}</p>
          <p className={typography.kpiValue} style={{ color: colors.text.primary }}>{opexPerCarat}<span className={typography.smallText} style={{ color: colors.text.tertiary, marginLeft: '4px' }}>USD</span></p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ backgroundColor: colors.status.negativeBg, color: colors.status.negative }}
            >
              <TrendingUp className="w-3 h-3" />
              +2.8% Orç.
            </span>
            <span 
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
              style={{ 
                backgroundColor: opexPerCaratChange > 0 ? colors.status.negativeBg : colors.status.positiveBg,
                color: opexPerCaratChange > 0 ? colors.status.negative : colors.status.positive
              }}
            >
              {opexPerCaratChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {opexPerCaratChange > 0 ? '+' : ''}{opexPerCaratChange.toFixed(1)}% YoY
            </span>
          </div>
        </div>
      </div>

      {/* Executive Summary - Premium Card - Análise por Rubrica */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custos operacionais e estrutura de OPEX' : 'Operating costs and OPEX structure'}
        language={language}
        maxHeight="550px"
        icon={Info}
        dataTestId="opex-executive-summary"
      >
        <div className="max-w-[900px] space-y-7">
            {/* Texto Introdutório */}
            <div 
              className="rounded-[12px] p-5 mb-6"
              style={{ 
                backgroundColor: colors.bg.surfaceAlt,
                border: `1px solid ${colors.border.soft}`
              }}
            >
              <p 
                className={typography.bodyText}
                style={{ color: colors.text.secondary }}
              >
                {summaryIntro[language]}
              </p>
            </div>

            {/* Análise por Rubrica */}
            {executiveAnalysisSections.map((section, idx) => {
              const IconComponent = section.icon;
              return (
                <div key={idx} className="group">
                  <h4 
                    className="text-[13px] font-semibold uppercase tracking-[0.5px] mb-3 flex items-center gap-2"
                    style={{ color: colors.text.primary }}
                  >
                    <IconComponent className="w-4 h-4" style={{ color: colors.accent.gold }} />
                    {section.title[language]}
                  </h4>
                  
                  {/* Intro */}
                  <p 
                    className={`${typography.bodyText} mb-3 font-medium`}
                    style={{ color: colors.text.primary }}
                  >
                    {section.intro[language]}
                  </p>
                  
                  {/* Face ao Homólogo */}
                  <div className="mb-3">
                    <span 
                      className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded mr-2"
                      style={{ backgroundColor: colors.bg.surfaceAlt, color: colors.text.tertiary }}
                    >
                      {language === 'pt' ? 'vs Homólogo' : 'vs Prior'}
                    </span>
                    <p 
                      className={`${typography.bodyText} mt-2`}
                      style={{ color: colors.text.secondary }}
                    >
                      {section.vsHomologo[language]}
                    </p>
                  </div>
                  
                  {/* Face ao Orçamento */}
                  <div className="mb-3">
                    <span 
                      className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded mr-2"
                      style={{ backgroundColor: colors.bg.surfaceAlt, color: colors.text.tertiary }}
                    >
                      {language === 'pt' ? 'vs Orçamento' : 'vs Budget'}
                    </span>
                    <p 
                      className={`${typography.bodyText} mt-2`}
                      style={{ color: colors.text.secondary }}
                    >
                      {section.vsOrcamento[language]}
                    </p>
                  </div>
                  
                  {/* Leitura de Gestão - Premium Callout */}
                  <div 
                    className="rounded-[10px] p-4 mt-3"
                    style={{ 
                      backgroundColor: colors.accent.goldBg,
                      border: `1px solid rgba(184, 154, 60, 0.15)`
                    }}
                  >
                    <p 
                      className="text-[11px] font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"
                      style={{ color: colors.accent.gold }}
                    >
                      <Target className="w-3.5 h-3.5" />
                      {language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}
                    </p>
                    <p 
                      className="text-[13px] leading-[20px]"
                      style={{ color: colors.text.secondary }}
                    >
                      {section.leituraGestao[language]}
                    </p>
                  </div>
                  
                  {/* Divider between sections */}
                  {idx < executiveAnalysisSections.length - 1 && (
                    <div 
                      className="border-t my-6" 
                      style={{ borderColor: colors.border.soft }}
                    />
                  )}
                </div>
              );
            })}
          </div>
      </ExecutiveSummarySection>

      {/* Category Detail Links - Moved below Executive Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Análise por Categoria' : 'Analysis by Category'}
        </h3>
        <div className="grid grid-cols-5 gap-4">
          {[
            { key: 'opex-cogs', name: language === 'pt' ? 'CMVMC' : 'COGS', icon: Package, color: 'text-gold-600' },
            { key: 'opex-personnel', name: language === 'pt' ? 'Custos com Pessoal' : 'Personnel Costs', icon: Users, color: 'text-primary' },
            { key: 'opex-subcontracts', name: language === 'pt' ? 'Subcontratos' : 'Subcontracts', icon: Truck, color: 'text-blue-600' },
            { key: 'opex-fse', name: language === 'pt' ? 'Gerais e Administrativos' : 'General & Administrative', icon: Building, color: 'text-emerald-600' },
            { key: 'opex-commercial', name: language === 'pt' ? 'Custos de Comercialização' : 'Commercial Costs', icon: BarChart3, color: 'text-purple-600' },
          ].map((item, idx) => (
            <button
              key={idx}
              onClick={() => {
                setActiveSection(item.key);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="p-6 border border-slate-200 rounded-xl hover:border-gold-400 hover:shadow-md transition-all text-left group min-h-[140px] flex flex-col justify-between"
            >
              <item.icon className={`w-8 h-8 ${item.color} mb-3 group-hover:scale-110 transition-transform`} />
              <div>
                <p className="text-sm font-semibold text-primary">{item.name}</p>
                <p className="text-xs text-slate-500 mt-2">{language === 'pt' ? 'Ver detalhe →' : 'View detail →'}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6">
        {/* Premium OPEX Breakdown Pie - Without detailed breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="font-serif text-lg font-semibold text-primary">
              {language === 'pt' ? 'Decomposição Custos Operacionais' : 'Operating Costs Breakdown'}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {language === 'pt' ? 'Distribuição por categoria de custo' : 'Distribution by cost category'}
            </p>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={pieData}
                cx="50%"
                cy="45%"
                innerRadius={60}
                outerRadius={95}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percentage }) => `${percentage}%`}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend 
                content={<CustomPieLegend />}
                verticalAlign="bottom"
              />
            </RechartsPie>
          </ResponsiveContainer>
        </div>

        {/* Dumbbell Chart - OPEX por Rubrica | 2025 vs 2024 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="font-serif text-lg font-semibold text-primary">
              {language === 'pt' ? `Custos Operacionais por Rubrica | ${year} vs ${year - 1}` : `Operating Costs by Category | ${year} vs ${year - 1}`}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {language === 'pt' ? 'Comparação face ao período homólogo' : 'Year-over-year comparison'}
            </p>
          </div>
          
          {/* Custom Dumbbell Chart */}
          <div className="space-y-3">
            {dumbbellData.map((item, idx) => {
              const maxValue = Math.max(...dumbbellData.map(d => Math.max(d.currentYear, d.prevYear)));
              const minDisplayValue = 0;
              const scale = (val) => ((val - minDisplayValue) / (maxValue - minDisplayValue)) * 100;
              
              const leftPos = scale(Math.min(item.prevYear, item.currentYear));
              const rightPos = scale(Math.max(item.prevYear, item.currentYear));
              const prevPos = scale(item.prevYear);
              const currPos = scale(item.currentYear);
              const isIncrease = item.currentYear > item.prevYear;
              
              return (
                <div key={idx} className="group">
                  <div className="flex items-center gap-4">
                    {/* Category name */}
                    <div className="w-32 flex-shrink-0">
                      <span className="text-sm font-medium text-slate-700">{item.name}</span>
                    </div>
                    
                    {/* Dumbbell visualization */}
                    <div className="flex-1 relative h-8">
                      {/* Background track */}
                      <div className="absolute inset-y-0 left-0 right-0 flex items-center">
                        <div className="w-full h-0.5 bg-slate-100 rounded-full"></div>
                      </div>
                      
                      {/* Connection line between dots */}
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full transition-all"
                        style={{ 
                          left: `${leftPos}%`, 
                          width: `${rightPos - leftPos}%`,
                          backgroundColor: isIncrease ? '#FEE2E2' : '#D1FAE5'
                        }}
                      ></div>
                      
                      {/* Previous year dot with tooltip */}
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer p-2"
                        style={{ left: `${prevPos}%` }}
                        onMouseEnter={() => setHoveredDot({ idx, type: 'prev', item })}
                        onMouseLeave={() => setHoveredDot(null)}
                        data-testid={`dumbbell-dot-prev-${idx}`}
                      >
                        <div 
                          className={`w-4 h-4 rounded-full border-2 bg-white transition-all ${hoveredDot?.idx === idx && hoveredDot?.type === 'prev' ? 'scale-150 shadow-lg' : 'hover:scale-125'}`}
                          style={{ borderColor: prevYearColor }}
                        ></div>
                        
                        {/* Tooltip for previous year */}
                        {hoveredDot?.idx === idx && hoveredDot?.type === 'prev' && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 pointer-events-none">
                            <div className="bg-[#0A192F] text-white rounded-lg px-3 py-2 shadow-xl border border-slate-700 whitespace-nowrap">
                              <p className="text-xs font-semibold text-slate-300 mb-1">{year - 1}</p>
                              <p className="text-sm font-bold text-white">{item.prevYear.toFixed(1)}M USD</p>
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                              <div className="border-4 border-transparent border-t-[#0A192F]"></div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Current year dot with tooltip */}
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer p-2"
                        style={{ left: `${currPos}%` }}
                        onMouseEnter={() => setHoveredDot({ idx, type: 'curr', item })}
                        onMouseLeave={() => setHoveredDot(null)}
                        data-testid={`dumbbell-dot-curr-${idx}`}
                      >
                        <div 
                          className={`w-4 h-4 rounded-full transition-all ${hoveredDot?.idx === idx && hoveredDot?.type === 'curr' ? 'scale-150 shadow-lg' : 'hover:scale-125'}`}
                          style={{ backgroundColor: currentYearColor }}
                        ></div>
                        
                        {/* Tooltip for current year */}
                        {hoveredDot?.idx === idx && hoveredDot?.type === 'curr' && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 pointer-events-none">
                            <div className="bg-[#0A192F] text-white rounded-lg px-3 py-2 shadow-xl border border-slate-700 whitespace-nowrap">
                              <p className="text-xs font-semibold text-gold-400 mb-1">{year}</p>
                              <p className="text-sm font-bold text-white">{item.currentYear.toFixed(1)}M USD</p>
                              <div className="border-t border-slate-600 mt-1 pt-1">
                                <p className={`text-xs ${isIncrease ? 'text-red-400' : 'text-emerald-400'}`}>
                                  {language === 'pt' ? 'vs ' : 'vs '}{year - 1}: {isIncrease ? '+' : ''}{item.change.toFixed(1)}M ({isIncrease ? '+' : ''}{item.changePercent.toFixed(1)}%)
                                </p>
                              </div>
                            </div>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                              <div className="border-4 border-transparent border-t-[#0A192F]"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Values */}
                    <div className="w-28 flex-shrink-0 text-right">
                      <span className="text-sm font-semibold text-primary">{item.currentYear.toFixed(1)}M</span>
                      <span className={`text-xs ml-2 ${isIncrease ? 'text-red-500' : 'text-emerald-600'}`}>
                        {isIncrease ? '+' : ''}{item.changePercent.toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-6 mt-6 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 bg-white" style={{ borderColor: prevYearColor }}></div>
              <span className="text-xs text-slate-500">{year - 1}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: currentYearColor }}></div>
              <span className="text-xs text-slate-500">{year}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded-full bg-red-100"></div>
              <span className="text-xs text-slate-500">{language === 'pt' ? 'Aumento' : 'Increase'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-1 rounded-full bg-emerald-100"></div>
              <span className="text-xs text-slate-500">{language === 'pt' ? 'Redução' : 'Decrease'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPEXDetail;
